import type { MenuProps } from 'antd';
import { getMenuItemKey, getMenuChildren } from './menuUtils';

type AntdMenuItem = NonNullable<Required<MenuProps>['items'][number]>;

/**
 * Контекст для legacy маршрутов устройств
 */
export interface LegacyDeviceRouteContext {
  path: string;
  inventorySectionTypeId?: string | null;
  typeDevice?: string | null;
}

/**
 * Маппинг legacy типов коммутаторов
 */
const switchLegacyTypeTargets: Record<string, string> = {
  managed: '/equipment/devices/switches?type=managed',
  soho: '/equipment/devices/switches?type=soho',
  unact: '/equipment/devices/switches?type=unact',
  notuplink: '/equipment/devices/switches?type=notuplink',
  notdnlink: '/equipment/devices/switches?type=notdnlink',
  all: '/equipment/devices/switches?type=all',
};

/**
 * Маппинг legacy типов радиооборудования
 */
const radioLegacyTypeTargets: Record<string, string> = {
  customers_devices: '/equipment/devices/radio?type=customers_devices',
  wo_customers_devices: '/equipment/devices/radio?type=wo_customers_devices',
  inactive_not_at_customer: '/equipment/devices/radio?type=inactive_not_at_customer',
  all: '/equipment/devices/radio?type=all',
};

/**
 * Правила переписывания legacy маршрутов устройств
 */
const legacyDeviceRouteRewrites: Array<{
  match: (context: LegacyDeviceRouteContext) => boolean;
  target: string;
}> = [
  {
    match: ({ inventorySectionTypeId, typeDevice, path }: LegacyDeviceRouteContext) =>
      path === '/equipment/devices' && !inventorySectionTypeId && !typeDevice,
    target: '/equipment/devices',
  },
  {
    match: ({ inventorySectionTypeId, typeDevice }: LegacyDeviceRouteContext) =>
      inventorySectionTypeId === '1' && !typeDevice,
    target: '/equipment/devices/switches',
  },
  {
    match: ({ inventorySectionTypeId, typeDevice }: LegacyDeviceRouteContext) =>
      inventorySectionTypeId === '1' && typeDevice === 'olt',
    target: '/equipment/devices/olt',
  },
  {
    match: ({ inventorySectionTypeId, typeDevice }: LegacyDeviceRouteContext) =>
      inventorySectionTypeId === '1' && typeDevice === 'onu',
    target: '/equipment/devices/onu',
  },
  ...Object.entries(switchLegacyTypeTargets).map(([type, target]) => ({
    match: ({ inventorySectionTypeId, typeDevice }: LegacyDeviceRouteContext) =>
      inventorySectionTypeId === '1' && typeDevice === type,
    target,
  })),
  {
    match: ({ inventorySectionTypeId }: LegacyDeviceRouteContext) => inventorySectionTypeId === '2',
    target: '/equipment/devices/radio',
  },
  ...Object.entries(radioLegacyTypeTargets).map(([type, target]) => ({
    match: ({ inventorySectionTypeId, typeDevice }: LegacyDeviceRouteContext) =>
      inventorySectionTypeId === '2' && typeDevice === type,
    target,
  })),
  {
    match: ({ inventorySectionTypeId }: LegacyDeviceRouteContext) => inventorySectionTypeId === '3',
    target: '/equipment/devices/medical',
  },
  {
    match: ({ inventorySectionTypeId }: LegacyDeviceRouteContext) => inventorySectionTypeId === '0',
    target: '/equipment/devices/system',
  },
  {
    match: ({ path }: LegacyDeviceRouteContext) => path === '/equipment/devices/switches',
    target: '/equipment/devices/switches',
  },
  {
    match: ({ path }: LegacyDeviceRouteContext) => path === '/equipment/devices/olt',
    target: '/equipment/devices/olt',
  },
  {
    match: ({ path }: LegacyDeviceRouteContext) => path === '/equipment/devices/onu',
    target: '/equipment/devices/onu',
  },
  {
    match: ({ path }: LegacyDeviceRouteContext) => path === '/equipment/devices/radio',
    target: '/equipment/devices/radio',
  },
  {
    match: ({ path }: LegacyDeviceRouteContext) => path === '/equipment/devices/medical',
    target: '/equipment/devices/medical',
  },
  {
    match: ({ path }: LegacyDeviceRouteContext) => path === '/equipment/devices/system',
    target: '/equipment/devices/system',
  },
  {
    match: ({ path }: LegacyDeviceRouteContext) => path === '/equipment/devices/cwdm',
    target: '/equipment/devices/cwdm',
  },
  {
    match: ({ path }: LegacyDeviceRouteContext) => path === '/equipment/devices/vlan',
    target: '/equipment/devices/vlan',
  },
];

/**
 * Нормализовать href устройства (legacy → новый формат)
 */
export function normalizeDeviceHref(href?: string): string | undefined {
  if (!href) {
    return undefined;
  }

  const isLegacyPath = href.startsWith('/devices');
  const normalizedBase = isLegacyPath ? href.replace('/devices', '/equipment/devices') : href;

  try {
    const url = new URL(normalizedBase, 'http://localhost');
    const inventorySectionTypeId =
      url.searchParams.get('inventory_section_type_id') ?? url.searchParams.get('inventorySectionTypeId');
    const typeDevice = url.searchParams.get('type_device') ?? url.searchParams.get('type');

    const routeContext: LegacyDeviceRouteContext = {
      path: url.pathname,
      inventorySectionTypeId,
      typeDevice,
    };

    for (const rewrite of legacyDeviceRouteRewrites) {
      if (rewrite.match(routeContext)) {
        return rewrite.target;
      }
    }

    const normalizedSearch = url.searchParams.toString();
    return normalizedSearch ? `${url.pathname}?${normalizedSearch}` : url.pathname;
  } catch {
    return normalizedBase;
  }
}

/**
 * Нормализовать ключ метки для перевода
 */
export function normalizeLabelKey(label?: string): string | undefined {
  if (!label) {
    return undefined;
  }
  return label
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * Построить карту родительских ключей меню
 */
export function buildMenuParentMap(
  items: AntdMenuItem[] | undefined,
  parentKey?: string,
  map: Map<string, string> = new Map(),
): Map<string, string> {
  if (!items) {
    return map;
  }

  items.forEach((item) => {
    if (!item) {
      return;
    }

    const key = getMenuItemKey(item);
    if (!key) {
      return;
    }

    if (parentKey) {
      map.set(key, parentKey);
    }

    const children = getMenuChildren(item);
    if (children && children.length > 0) {
      buildMenuParentMap(children, key, map);
    }
  });

  return map;
}

/**
 * Извлечь ключи корневых пунктов меню (с дочерними элементами)
 */
export function extractRootMenuKeys(items: AntdMenuItem[] | undefined): string[] {
  if (!items) {
    return [];
  }

  return items
    .map((item) => {
      if (!item) {
        return undefined;
      }

      const key = getMenuItemKey(item);
      const hasChildren = getMenuChildren(item) !== undefined;

      return hasChildren && key ? key : undefined;
    })
    .filter((key): key is string => Boolean(key));
}

/**
 * Дедуплицировать пункты меню
 */
export function deduplicateMenuItems(items: AntdMenuItem[]): AntdMenuItem[] {
  if (!items.length) {
    return [];
  }

  const seen = new Set<string>();

  const dedupe = (nodes: AntdMenuItem[], parentKey?: string): AntdMenuItem[] =>
    nodes.reduce<AntdMenuItem[]>((acc, node, index) => {
      if (!node) {
        return acc;
      }

      const resolvedKey =
        node.key != null && node.key !== ''
          ? String(node.key)
          : `${parentKey ?? 'devices'}::${index}`;

      if (seen.has(resolvedKey)) {
        if (import.meta.env.DEV) {
          console.warn(
            `[Sidebar] duplicate key "${resolvedKey}" discarded (parent: ${parentKey ?? 'root'})`,
          );
        }
        return acc;
      }

      seen.add(resolvedKey);

      const childItems = getMenuChildren(node);
      const dedupChildren = childItems ? dedupe(childItems, resolvedKey) : undefined;

      const nextNode: AntdMenuItem =
        dedupChildren && 'children' in node
          ? ({ ...node, children: dedupChildren } as AntdMenuItem)
          : node;

      acc.push(nextNode);

      return acc;
    }, []);

  return dedupe(items);
}

